import styled from 'styled-components';
import { useContext, useState, useEffect } from 'react';
import ButtonDiamondsLoading from 'components/common/buttons/button-diamonds-loading';
import { Icon } from 'components/common/icon/icon';
import { Link } from 'components/common/link';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { priceListPlans, steps } from 'utils/package-helper';
import { FormikProps } from 'formik';
import { TranslationContext } from 'contexts/translation-context';
import { JoinFormType, PackagePrices } from 'types/join';

import { Col, Row } from 'components/common/grid';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useSelector } from 'react-redux';
import { errorList, errorListTypes } from 'constants/error-list';
import RegistrationStepper from './registrationStepper';
import { Details } from './details';
import { PaymentDetailsSection } from './payment-details';
import { NewsLettersMoreDetailsContent } from './news-letters-more-details-toggle';
import SmsTermsMoreDetailsContent from './sms-terms-more-details';
import { PlanState } from '../reducers/plan';

const TitleContainer = styled.div`
  font-size: 24px;
  font-family: ${props => props.theme.fonts.montserrat};
  text-align: center;
`;
const PlanWrapper = styled.div`
  margin-top: 40px;
`;

const ErrorIcon = styled('span')(({ theme }) => ({
  backgroundColor: (theme.palette as any).red,
  width: '16px',
  height: '16px',
  color: 'white',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 'bold',
  marginLeft: '4px'
}));

type CheckProps = { isVisible: boolean };

const Check = styled(Icon).attrs({
  name: 'check'
})<CheckProps>`
  color: #009ed3;
  font-size: 1.2rem;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.white};
  width: 12px;
  height: 12px;
  margin-left: -5px;
  visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};
  margin-right: 3px;
`;

const StyledCol = styled(Col)`
  margin-right: 120px;
`;
const RowDetails = styled.div`
  width: 655px;
  background-color: ${props => props.theme.colors.white};
  padding: 12px;
  border: 1px solid ${props => props.theme.colors.lightGrey};
  border-radius: 2px;
`;
const Important = styled.span`
  font-size: 13px;
  font-family: ${props => props.theme.fonts.montserratSemiBold};
  color: ${props => props.theme.colors.siteBlack};
  margin-right: 5px;
`;
const ImportantText = styled.div`
  font-family: ${props => props.theme.fonts.montserratMedium};
`;
const ConfirmOrderDetails = styled.div`
  font-size: 11px;
  margin-left: 5px;
  margin-bottom: 22px;
  color: ${props => props.theme.colors.siteBlack};
  /* display: flex; */
`;
const CheckboxContainer = styled.div<{ disabled: boolean; isPriceList?: boolean }>`
  margin-top: ${props => (props.isPriceList ? '5px' : '13px')};
  margin-left: 5px;
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
`;

const CheckboxLabel = styled.label`
  display: flex;
  font-size: 13px;
  margin-left: 5px;
  margin-top: 4px;
  color: #434343;
  font-family: ${props => props.theme.fonts.montserratMedium};
`;
const ConfirmButton = styled(ButtonDiamondsLoading)`
  width: 204px;
  height: 40px;
  border-radius: 3px;
  margin-top: 25px;
`;
const ButtonText = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;
const StyledLink = styled(Link)`
  text-decoration: underline !important;
  font-size: 13px;
  margin-left: 4px;
`;
const StyledCheckbox = styled(Checkbox)<{ error?: boolean }>`
  && {
    ${props =>
      props.error &&
      `
      & .MuiCheckbox-root {
        border: 2px solid ${props.theme.colors.red};
        border-radius: 4px; /* Optional: Adjust as needed */
      }
      
      & .MuiSvgIcon-root {
        color: ${props.theme.colors.red};
      }
    `};
    & .MuiSvgIcon-root {
      width: 16px;
      height: 16px;
    }
    & .MuiFormControlLabel-label {
      font-family: ${props => props.theme.fonts.montserratMedium};
    }
  }
`;
const Asterix = styled.span`
  color: ${props => props.theme.colors.red};
  margin-left: 3px;
  ::before {
    content: '*';
  }
`;
const Line = styled.div`
  width: 655px;
  margin-top: 25px;
  margin-bottom: 25px;
  border-bottom: 1px solid ${props => props.theme.colors.lightGrey};
`;

type JoinFormProps = {
  form: FormikProps<JoinFormType>;
  isConfirmed: boolean;
  subscriptionId: string | null;
  numConfirmClicked: number;
  selectedPlan?: PackagePrices;
  setIsConfirmed: (value: boolean) => void;
  setShowSuccessPage: (value: boolean) => void;
  setShowSuccessPageForPriceList: (value: boolean) => void;
  setContactMEThankYouScreenForPriceList: (value: boolean) => void;
  isYourPlanActive: boolean;
  disabled?: boolean;
  removeCacheForm: () => void;
  isPriceList: boolean;
};

export const JoinForm = ({
  form,
  isConfirmed,
  selectedPlan,
  subscriptionId,
  numConfirmClicked,
  setIsConfirmed,
  setShowSuccessPage,
  setShowSuccessPageForPriceList,
  setContactMEThankYouScreenForPriceList,
  isYourPlanActive,
  disabled,
  removeCacheForm,
  isPriceList
}: JoinFormProps) => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const [proceedToPaymentClicked, setProceedToPaymentClicked] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleFormSubmit = async (event: React.FormEvent) => {
    setProceedToPaymentClicked(true);
    event.preventDefault();
    try {
      // Execute reCAPTCHA
      if (!executeRecaptcha) {
        console.error('reCAPTCHA not loaded');
        setProceedToPaymentClicked(false);
        return;
      }
      const token = await executeRecaptcha('register'); // Action name is 'register'
      form.setFieldValue('recaptcha', token);
      form.submitForm();
      setProceedToPaymentClicked(false);
    } catch (error) {
      console.error(error);
      setProceedToPaymentClicked(false);
    }
  };

  return (
    <form onSubmit={handleFormSubmit} onReset={form.handleReset}>
      <TitleContainer>
        {isPriceList
          ? TRANSLATION_TEXT.create_your_rapaport_account
          : TRANSLATION_TEXT.create_your_rapnet_account}
      </TitleContainer>
      <PlanWrapper>
        <RegistrationStepper
          activeStep={!isConfirmed || isYourPlanActive ? 0 : 1}
          steps={[
            {
              title: 'Account Details',
              content: (
                <StyledCol>
                  <Details
                    form={form}
                    disabled={isConfirmed}
                    isPriceList={isPriceList}
                    selectedPlan={selectedPlan}
                  />
                  <RowDetails>
                    {!isPriceList && (
                      <ConfirmOrderDetails>
                        <ImportantText>
                          <Important>{TRANSLATION_TEXT.important_title}</Important>
                          {TRANSLATION_TEXT.confirm_order_details}
                        </ImportantText>
                      </ConfirmOrderDetails>
                    )}
                    <CheckboxContainer disabled={isConfirmed} isPriceList={isPriceList}>
                      <FormControlLabel
                        control={
                          <StyledCheckbox
                            name="agree"
                            checked={form.values.agree}
                            onChange={form.handleChange}
                            error={form.touched.agree && Boolean(form.errors.agree)}
                          />
                        }
                        label={
                          <CheckboxLabel htmlFor="agree">
                            {priceListPlans.includes(selectedPlan?.packageName || '')
                              ? TRANSLATION_TEXT.agree_to_rapaport
                              : TRANSLATION_TEXT.agree_to_rapnet}
                            <StyledLink
                              href={
                                priceListPlans.includes(selectedPlan?.packageName || '')
                                  ? 'https://www.diamonds.net/legal/licensingagreement.aspx'
                                  : 'https://www.rapnet.com/terms/license-agreement/'
                              }
                              external
                              newTab
                            >
                              {TRANSLATION_TEXT.terms_and_conditions}
                            </StyledLink>
                            <Asterix />
                            {form.touched.agree && Boolean(form.errors.agree) && (
                              <ErrorIcon>!</ErrorIcon>
                            )}
                          </CheckboxLabel>
                        }
                      />
                    </CheckboxContainer>
                    <SmsTermsMoreDetailsContent form={form} disabled={isConfirmed} />
                    <NewsLettersMoreDetailsContent form={form} disabled={isConfirmed} />
                  </RowDetails>
                  <ConfirmButton
                    blue={!isConfirmed}
                    green={isConfirmed}
                    submit
                    ids={[
                      'CREATE_SUBSCRIPTION_REQUEST',
                      'UPDATE_SUBSCRIPTION_MEMBER_DETAILS',
                      'GET_CURRENT_CONTRACT',
                      'UPDATE_NEWS_LETTERS'
                    ]}
                    disabled={isConfirmed || proceedToPaymentClicked}
                    loadingText={TRANSLATION_TEXT.polishing_your_membership_request}
                    errorClickable={!subscriptionId && numConfirmClicked > 1}
                  >
                    <ButtonText>
                      {isConfirmed && <Check isVisible={isConfirmed} />}
                      {isConfirmed
                        ? TRANSLATION_TEXT.order_confirmed
                        : TRANSLATION_TEXT.proceed_to_payment}
                    </ButtonText>
                  </ConfirmButton>

                  <Line />
                </StyledCol>
              )
            },
            {
              title: 'Payment',
              content: (
                <PaymentDetailsSection
                  id="paymentSection"
                  isConfirmed={isConfirmed}
                  setIsConfirmed={setIsConfirmed}
                  setShowSuccessPage={setShowSuccessPage}
                  setShowSuccessPageForPriceList={setShowSuccessPageForPriceList}
                  isYourPlanActive={isYourPlanActive}
                  subscriptionType={selectedPlan?.subscriptionType}
                  setContactMEThankYouScreenForPriceList={setContactMEThankYouScreenForPriceList}
                  form={form}
                  isPriceList={isPriceList}
                  selectedPlan={selectedPlan}
                  removeCacheForm={removeCacheForm}
                />
              )
            }
          ]}
        />
      </PlanWrapper>
    </form>
  );
};
